import { condition as makeCondition } from '@ofmos/shared/filter-ast';
import { useDeferredValue, useMemo, useState } from 'react';
import type { FieldMetadata } from '../../data/client';
import { useObject, useRecordList } from '../../data/hooks';
import { useRelationLabel } from '../../fields/useRelationLabel';
import { Button, SearchField, Skeleton } from '../../ui';
import { PickerOption } from '../record/related/PickerOption';

/**
 * THE RELATION OPERAND (D20), and it is the D5 picker's mechanism rather than
 * `RelationInput`'s.
 *
 * `RelationInput` fetches a flat fifty records with no filter and the server's default
 * order - its own docblock marks that ceiling. Filtering "Creators by Agency" through a
 * control that cannot find the 251st agency would be a filter editor that works on small
 * objects and lies on large ones.
 *
 * So this searches: `contains(<label field>, term)` with a small limit, through the
 * shared AST and the same `POST /api/query` as everything else. Once a record is chosen
 * it shows its NAME with a way back to the search - a uuid in a filter row is a value
 * nobody can check.
 */
const OPERAND_RESULT_LIMIT = 10;

export interface RelationOperandInputProps {
  readonly field: FieldMetadata;
  /** The chosen record's id, or '' for "not chosen yet". */
  readonly value: string;
  readonly onChange: (value: string) => void;
}

export function RelationOperandInput({ field, value, onChange }: RelationOperandInputProps) {
  const [term, setTerm] = useState('');
  const deferredTerm = useDeferredValue(term);
  const targetObjectId = field.options?.relation?.targetObjectId ?? '';

  const target = useObject(targetObjectId);
  const labelFieldId = target.data?.labelIdentifierFieldId ?? '';
  const chosen = useRelationLabel(field, value === '' ? null : value);

  const trimmed = deferredTerm.trim();
  const filter = useMemo(
    () =>
      trimmed === '' || labelFieldId === ''
        ? undefined
        : makeCondition(labelFieldId, 'contains', trimmed),
    [trimmed, labelFieldId],
  );

  const results = useRecordList(targetObjectId, {
    select: labelFieldId === '' ? [] : [labelFieldId],
    filter,
    ...(labelFieldId === '' ? {} : { orderBy: [{ fieldId: labelFieldId, direction: 'asc' }] }),
    limit: OPERAND_RESULT_LIMIT,
    enabled: value === '' && labelFieldId !== '',
  });

  if (value !== '') {
    return (
      <div className="flex items-center justify-between gap-2 rounded-control bg-surface-sunken px-2 py-1.5">
        <span className="min-w-0 truncate text-sm text-ink">
          {chosen.isPending ? <Skeleton className="w-24" /> : (chosen.label ?? 'One record')}
        </span>
        <Button variant="ghost" size="sm" onClick={() => onChange('')}>
          Change
        </Button>
      </div>
    );
  }

  if (labelFieldId === '' && !target.isPending) {
    return (
      <p className="text-sm text-ink-muted">
        {`${target.data?.labelPlural ?? 'These records'} have no record title yet, so there is nothing to search on.`}
      </p>
    );
  }

  const ids = results.data?.ids ?? [];

  return (
    <div className="space-y-2">
      <SearchField
        context="content"
        label="Search for a record"
        placeholder={`Search ${target.data?.labelPlural.toLowerCase() ?? 'records'}`}
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      {results.isPending ? (
        <Skeleton className="h-9" label="Searching" />
      ) : ids.length === 0 ? (
        <div>
          <p className="text-sm text-ink">No results</p>
          <p className="text-sm text-ink-muted">
            {trimmed === ''
              ? 'No records are available to choose.'
              : `Nothing matches "${trimmed}".`}
          </p>
        </div>
      ) : (
        <ul className="max-h-40 divide-y divide-line-subtle overflow-y-auto">
          {ids.map((id) => (
            <PickerOption
              key={id}
              objectId={targetObjectId}
              recordId={id}
              labelFieldId={labelFieldId}
              disabled={false}
              onPick={() => {
                onChange(id);
                setTerm('');
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
